import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { getUnsubscribeToken, unsubscribeCustomer } from "../../api/customer";
import { getCustomFormData } from "../../api/customForm";
import { Form } from "antd";
import { useIntl } from "react-intl";
import { getMerchantCode } from "../../components/customFormComponents/utils/commonFun";
import { useFormRecaptcha } from "../../hooks/useFormRecaptcha";

const emailFormat = /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const useUnsubscribePage = props=>{
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const { formatMessage } = useIntl();

    const [form] = Form.useForm();
    const {pathname} = useLocation();
    const {merchant_code} = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const token = searchParams.get('token');
    const [topics, setTopics] = useState([]);
    const [selectTopics, setSelectTopics] = useState([]);

    const [formData, setFormData] = useState(null);
    const [formClientWidth, setFormClientWidth] = useState(1024);

    const [htmlData, setHtmlData] = useState({
        title: null,
        favicon: null
    })

    const [recaptchaConfig, setRecaptchaConfig] = useState(null);
    // [reCAPTCHA] reCAPTCHA config is read from the public form schema.
    const recaptcha = useFormRecaptcha(recaptchaConfig);

    const handleGetRules = useCallback((isRequired, validate, fieldName, otherValidate)=>{
        let rules = [];
        if(isRequired) {
            rules.push({
                required: true, message: formatMessage({
                    id: "form.isRequired", 
                    defaultMessage: "{name} is required!"
                }, {
                    name: fieldName
                })
            })
        }
        if(validate === 'email') {
            rules.push({
                pattern: emailFormat,
                message: formatMessage({
                    id: "form.isEmail", 
                    defaultMessage: "Please enter a valid email account"
                })
            })
        }
        return rules;
    }, [formatMessage])

    const handleGetTopicList = useCallback(()=>{
        setLoading(true);
        const merchantCode = merchant_code === 'uat_test' ? 'contact/' : `api/merchant/${merchant_code}/contact/`;
        getUnsubscribeToken({
            token: token
        }, merchantCode).then(res=>{
            if(res?.topics?.length > 0) {
                setTopics(res.topics.map(topic=>({
                    label: topic.topicLabel,
                    value: topic.topicCode
                })));
            }
            else {
                setResult(res?.message || 'No topics found');
                setTopics([]);
            }
            setSelectTopics([]);
            setLoading(false);
        }).catch(error=>{
            setLoading(false);
            setResult(error.response?.data?.message || error.message);
            setSelectTopics([]);
            setTopics([]);
        })
    }, [merchant_code, token]);

    const handleReloadTopics = useCallback(()=>{
        setSelectTopics([]);
        setTopics([]);
        setResult(null);
        form.setFieldValue('topics', []);
        handleGetTopicList();
    }, [form, handleGetTopicList]);

    const handleConfirmUnSubscribe = useCallback(async ()=>{
        if(selectTopics?.length) {
            setLoading(true);
            setResult(null);

            // [reCAPTCHA] obtain proof (v2/v3) before sending the submission.
            let recaptchaProof = null;
            try {
                recaptchaProof = await recaptcha.getSubmitProof();
            } catch (e) {
                setLoading(false);
                setResult(e?.message || 'reCAPTCHA verification failed');
                return;
            }

            const merchantCode = merchant_code === 'uat_test' ? 'contact/' : `api/merchant/${merchant_code}/contact/`;
            const body = {
                token: token,
                topics: selectTopics,
                ...(recaptchaProof ? { recaptcha: recaptchaProof } : {}),
            };
            try {
                let res = await unsubscribeCustomer(body, merchantCode);

                // [reCAPTCHA] v3 low score -> backend requests a v2 challenge.
                if(res?.errorCode === 'RECAPTCHA_CHALLENGE_REQUIRED' && res?.recaptchaChallenge) {
                    recaptcha.renderChallenge(res.recaptchaChallenge);
                    if(res.recaptchaChallenge.widget === 'invisible') {
                        const v2Proof = await recaptcha.getSubmitProof();
                        res = await unsubscribeCustomer({ ...body, recaptcha: v2Proof }, merchantCode);
                    } else {
                        setLoading(false);
                        setResult('Please complete the reCAPTCHA verification and submit again.');
                        return;
                    }
                }

                if(res?.result) {
                    setResult(true);
                    // [reCAPTCHA] reset v2 widget and clear fallback challenge after success.
                    recaptcha.clearChallenge();
                }
                else {
                    const errCode = res?.errorCode;
                    if(errCode === 'RECAPTCHA_INVALID' || errCode === 'RECAPTCHA_REQUIRED') {
                        recaptcha.reset();
                    }
                    setResult(res?.message || 'Unsubscribe failed');
                }
            } catch (error) {
                setResult(error.response?.data?.message || error.message);
            }
            setLoading(false);
        }
        else if(topics?.length){
            setResult("Please select at least one topic to unsubscribe!");
        }
    }, [merchant_code, token, selectTopics, topics, recaptcha]);

    const handleGetFormSchema = useCallback(()=>{
        const merchantCode = getMerchantCode(merchant_code, 'form');
        getCustomFormData({
            formCode: 'unSubscription-UnSubscriptionForm',
            locale: 'global'
        }, merchantCode ? merchantCode : merchant_code, merchantCode ? null : 'form').then(res => {
            if(res?.status === '200') {
                setHtmlData({
                    title: res.data?.title || null,
                    favicon: res.data?.favicon || null
                })
                setFormData(res.data?.form);
                // [reCAPTCHA] read reCAPTCHA runtime config from public schema.
                setRecaptchaConfig(res.data?.recaptcha || null);
            }
            else {
                setFormData(undefined);
            }
        }).catch(error=>{
            setFormData(undefined);
        })
    }, [merchant_code]);

    useEffect(()=>{
        if(pathname.includes('/unsubscribe') && token && merchant_code) {
            handleGetFormSchema();
            handleGetTopicList();
        }
        else {
            setSelectTopics([]);
        }
    }, [token, pathname, merchant_code])

    return {
        loading,
        merchant_code,
        formData,
        result,
        setResult,
        topics,
        selectTopics,
        setSelectTopics,
        form,
        handleGetRules,
        handleConfirmUnSubscribe,
        handleReloadTopics,
        formClientWidth,
        setFormClientWidth,
        htmlData,
        recaptcha
    }
}